import { Grid, Avatar, Typography, Box, Modal, Button } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { StoreState } from "../../../models/reduxModels";
import { userDetails } from "../../../models/userModels";
import { environment } from "../../../environment";
import {
  LogOutAction,
  SidenavAutoCloseOpen,
  resetPasswordAction,
} from "../../../stores/actions/userAction";
import { useDispatch } from "react-redux";
import {
  User,
  LogOut,
  LayoutDashboard,
  Building2,
  Users,
  Fingerprint,
  UserCog,
  PlusCircle,
  List,
  Clock,
  Tag,
  Plus,
  Search,
  AlertCircle,
  FileText,
  BookOpen,
  LayoutGrid,
  CalendarCheck,
  Award,
  Upload,
  UserPlus,
  Circle,
  ShieldCheck,
  UsersRound,
  Workflow,
  GitCompareArrows,
  FileWarning,
  FileSpreadsheet,
  DownloadIcon,
  CalendarX2,
  CalendarArrowDown,
  Table2,
  Megaphone,
} from "lucide-react";

const menuIconMap: Record<string, React.ElementType> = {
  dashboard: LayoutDashboard,
  business: Building2,
  add_circle_outline: PlusCircle,
  supervisor_account: Users,
  format_list_bulleted: List,
  pix: Tag,
  schedule: Clock,
  people: Users,
  toc: List,
  add: Plus,
  loupe: Search,
  pending_action: AlertCircle,
  collections_bookmark: BookOpen,
  fingerprint: Fingerprint,
  apps: CalendarCheck,
  summarize: FileText,
  report: FileSpreadsheet,
  punch_clock: Clock,
  priority_high: AlertCircle,
  role_master: ShieldCheck,
  user_role_mapping: UsersRound,
  life_cycle_management: Workflow,
  award: Award,
  upload: Upload,
  user_plus: UserPlus,
  compare_arrows: GitCompareArrows,
  file_warning: FileWarning,
  download: DownloadIcon,
  Wrong_attendance: CalendarX2,
  attendance_download: CalendarArrowDown,
  work_order: Table2,
  broadcast: Megaphone,
};

const moduleIconMap: Record<string, React.ElementType> = {
  dashboard: LayoutDashboard,
  organization_management: Building2,
  employee_management: Users,
  attendance_management: Fingerprint,
  user_management: UserCog,
};

const SideNav = ({
  user,
  side_nav,
  SidenavAutoCloseOpen,
  resetPasswordAction,
  logoutEvent,
}: SideNavProps) => {
  const sideNav = environment.sideNav;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const isOrangeTheme = [
    "dashboard",
    "organization",
    "employment",
    "attendance",
    "usermanagement",
  ].includes(location.pathname.split("/")[1]?.toLowerCase());

  const sidebarBgClass = isOrangeTheme ? "bg-[#FFFBF7]" : "bg-sidebarBg";
  const sidebarTextClass = isOrangeTheme
    ? "text-[#4B5563]"
    : "text-sidebarText";
  const sidebarHoverClass = isOrangeTheme
    ? "hover:bg-[#F9FAFB] hover:text-[#FF6B1A]"
    : "hover:bg-sidebarHover hover:text-white";
  const sidebarDividerClass = isOrangeTheme
    ? "border-[#E5E7EB]"
    : "border-sidebarDivider";
  const sidebarActiveClass = isOrangeTheme
    ? "bg-[#FFEFE6] text-[#FF6B1A]"
    : "bg-primary text-sidebarActiveText";

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState<boolean>(false);
  const navigateTo = (url: any) => {
    navigate(url, { state: { fromSideNav: true } });
    if (!isExpanded) setIsExpanded(true);
  };

  const navigateToProfile = () => {
    const empId = user?.employee_detail?.employee_id;
    if (empId) {
      navigate(`/employment/manage-profile/${empId}`);
    }
  };

  const assesCheck = (element: any) =>
    !!user?.assess_control?.find((n) => n.access_control_name === element.key);

  const currentMenuGroup = sideNav.find(
    (m) => m.url.split("/")[1] === location.pathname.split("/")[1],
  );

  const [isExpanded, setIsExpanded] = useState(false);
  const [isMouseNear, setIsMouseNear] = useState(false);
  const sidebarRef = useRef<any>(null);
  const leaveTimer = useRef<any>(null);

  useEffect(() => {
    const handleMouseMove = (e: any) => {
      if (!sidebarRef.current) return;

      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const isNear =
        e.clientX <= sidebarRect.right + 50 &&
        e.clientX >= sidebarRect.left - 50 &&
        e.clientY >= sidebarRect.top - 50 &&
        e.clientY <= sidebarRect.bottom + 50;

      setIsMouseNear(isNear);

      if (isNear) {
        setIsExpanded(true);
        if (leaveTimer.current) {
          clearTimeout(leaveTimer.current);
          leaveTimer.current = null;
        }
      } else {
        if (!leaveTimer.current) {
          leaveTimer.current = setTimeout(() => {
            setIsExpanded(false);
            leaveTimer.current = null;
          }, 100);
        }
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      if (leaveTimer.current) {
        clearTimeout(leaveTimer.current);
      }
    };
  }, []);
  const LogOutDetailsAction = () => {
    setIsLogoutModalOpen(true);
  };
  const FinalLogout = () => {
    dispatch(LogOutAction() as any);
  };

  const renderMenuIcon = (item: any) => {
    const IconComponent =
      menuIconMap[item.icon] || moduleIconMap[item.key] || Circle;
    return <IconComponent className="w-5 h-5" />;
  };

  return (
    <div
      ref={sidebarRef}
      className={`h-screen flex flex-col ${sidebarBgClass} ${sidebarTextClass} transition-all duration-300 ${
        isExpanded ? "w-[260px]" : "w-16"
      } relative overflow-hidden border-r ${sidebarDividerClass}`}
    >
      {/* Logo */}
      <div
        className={`flex items-center justify-center h-16 px-4 border-b ${sidebarDividerClass} shrink-0`}
      >
        <div className="bg-white p-1.5 rounded-md shadow-sm">
          <img
            src={require("../../../assets/minimalistic-logo.png")}
            alt="Logo"
            className="h-12 w-12 object-contain"
          />
        </div>
        {isExpanded && (
          <span className={`ml-3 text-sm font-semibold tracking-wide ${isOrangeTheme ? "text-[#FF6B1A]" : "text-white/90"} truncate`}>
            IAIMS
          </span>
        )}
      </div>

      {location.pathname === "/dashboard/dashboard-main/" && (
        <div className={`p-3 border-b ${sidebarDividerClass} shrink-0`}>
          <div
            className={`flex items-center cursor-pointer rounded-md ${isOrangeTheme ? "hover:bg-[#F9FAFB]" : "hover:bg-sidebarHover"} transition-colors p-2 ${isExpanded ? "gap-3" : "flex-col gap-1"}`}
            onClick={navigateToProfile}
            title={!isExpanded ? "View Profile" : undefined}
          >
            <div className="relative shrink-0">
              <div
                className={`h-10 w-10 rounded-full ${isOrangeTheme ? "bg-gray-100 border border-gray-200" : "bg-white/10 border border-white/20"} flex items-center justify-center`}
              >
                <User
                  className={`h-5 w-5 ${isOrangeTheme ? "text-gray-600" : "text-white/80"}`}
                />
              </div>
              <div
                className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-success border-2 ${isOrangeTheme ? "border-[#FFFBF7]" : "border-sidebarBg"}`}
              />
            </div>
            {isExpanded && (
              <div className="min-w-0 flex-1">
                <p
                  className={`text-sm font-semibold ${isOrangeTheme ? "text-gray-800" : "text-white"} truncate`}
                >
                  {user?.employee_detail?.employee_name || "User Name"}
                </p>
                <p
                  className={`text-xs ${isOrangeTheme ? "text-gray-500" : "text-white/60"} truncate`}
                >
                  {user?.employee_detail?.employee_id || "Emp ID"}
                </p>
                <p
                  className={`text-xs ${isOrangeTheme ? "text-gray-500" : "text-white/60"} truncate`}
                >
                  {user?.designation_name || "Designation"}
                </p>
                <div
                  className="flex items-center gap-1 mt-1 cursor-pointer group"
                  onClick={(e) => {
                    e.stopPropagation();
                    LogOutDetailsAction();
                  }}
                  title="Sign out"
                >
                  <LogOut className="w-3.5 h-3.5 text-primary group-hover:text-primaryHover" />
                  <span className="text-xs font-medium text-primary group-hover:text-primaryHover">
                    Sign Out
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Menu */}
      <div className="flex-1 overflow-y-auto py-2 px-1.5 space-y-0.5">
        {currentMenuGroup?.menuItem
          .filter((m: any) => !m?.isnotmenu)
          .map((item, index) => {
            const isItemActive =
              location.pathname === item.url ||
              item.sub_url?.some((url: string) =>
                location.pathname.includes(url),
              );
            if (!assesCheck(item))
              return <React.Fragment key={index}></React.Fragment>;
            return (
              <div key={index}>
                <div
                  className={`flex items-center cursor-pointer transition-colors duration-200
                  ${
                    isItemActive
                      ? sidebarActiveClass
                      : `${sidebarTextClass} ${sidebarHoverClass}`
                  }
                  ${isExpanded ? "px-3 py-2.5 rounded-md gap-3" : "justify-center px-2 py-2.5 rounded-md"}`}
                  onClick={() => navigateTo(item.url)}
                  title={!isExpanded ? item.item_name : undefined}
                >
                  <div className="w-5 flex justify-center shrink-0">
                    {renderMenuIcon(item)}
                  </div>
                  <span
                    className={`text-xs font-medium whitespace-nowrap transition-all duration-200
                    ${isExpanded ? "translate-x-0 opacity-100" : "hidden"}`}
                  >
                    {item.item_name}
                  </span>
                </div>
              </div>
            );
          })}
      </div>

      {/* Bottom profile */}
      {location.pathname !== "/dashboard/dashboard-main/" && (
        <div className={`p-2 border-t ${sidebarDividerClass} shrink-0`}>
          <div
            className={`flex cursor-pointer rounded-md ${isOrangeTheme ? "hover:bg-[#F9FAFB]" : "hover:bg-sidebarHover"} transition-colors p-1 ${isExpanded ? "flex-row gap-2 items-center" : "flex-col items-center gap-1"}`}
            onClick={navigateToProfile}
            title={!isExpanded ? "View Profile" : undefined}
          >
            <div className="relative shrink-0">
              <Avatar
                src="/static/images/avatar/2.jpg"
                className={`h-8 w-8 border ${isOrangeTheme ? "border-gray-200" : "border-white/20"}`}
              />
              <div
                className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-success border-2 ${isOrangeTheme ? "border-[#FFFBF7]" : "border-sidebarBg"}`}
              />
            </div>
            {isExpanded && (
              <div className="flex flex-col min-w-0 flex-1">
                <p
                  className={`text-sm font-semibold ${isOrangeTheme ? "text-gray-800" : "text-white"} truncate`}
                >
                  {user?.employee_detail?.employee_name || "User Name"}
                </p>
                <p
                  className={`text-xs ${isOrangeTheme ? "text-gray-500" : "text-white/60"} truncate`}
                >
                  {user?.employee_detail?.employee_id || "Emp ID"}
                </p>
                <p
                  className={`text-xs ${isOrangeTheme ? "text-gray-500" : "text-white/60"} truncate`}
                >
                  {user?.designation_name || "Designation"}
                </p>
                <div
                  className="flex items-center gap-1 mt-1 cursor-pointer group"
                  onClick={(e) => {
                    e.stopPropagation();
                    LogOutDetailsAction();
                  }}
                  title="Sign out"
                >
                  <LogOut className="w-3.5 h-3.5 text-primary group-hover:text-primaryHover" />
                  <span className="text-xs font-medium text-primary group-hover:text-primaryHover">
                    Sign Out
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <Modal
        open={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            width: { xs: "90%", sm: "400px" },
            backgroundColor: "var(--kinetic-surface)",
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            borderRadius: "var(--kinetic-dialog-radius)",
            overflow: "hidden",
            border: "1px solid var(--kinetic-outline)",
            boxShadow: "var(--kinetic-dialog-shadow)",
          }}
        >
          <Grid
            container
            sx={{
              backgroundColor: "var(--kinetic-primary)",
              px: 2,
              py: 1.5,
            }}
            justifyContent={"flex-start"}
            alignItems={"center"}
          >
            <Typography
              sx={{ fontWeight: 600, color: "var(--kinetic-on-primary)" }}
            >
              Logout
            </Typography>
          </Grid>
          <Box sx={{ p: 2 }}>
            <Typography className="text-sm text-onSurfaceVariant">
              Are you sure you want to log out of the portal?
            </Typography>
            <Grid
              display={"flex"}
              justifyContent={"flex-end"}
              alignSelf={"center"}
              marginTop={2}
              gap={1}
            >
              <Button
                sx={{
                  color: "var(--kinetic-primary)",
                  borderColor: "var(--kinetic-primary)",
                  textTransform: "none",
                  borderRadius: "var(--kinetic-button-radius)",
                }}
                onClick={() => setIsLogoutModalOpen(false)}
                variant="outlined"
                size="small"
              >
                Cancel
              </Button>
              <Button
                sx={{
                  color: "var(--kinetic-on-primary)",
                  backgroundColor: "var(--kinetic-primary)",
                  textTransform: "none",
                  borderRadius: "var(--kinetic-button-radius)",
                  "&:hover": {
                    backgroundColor: "var(--kinetic-primary-hover)",
                  },
                }}
                variant="contained"
                size="small"
                onClick={FinalLogout}
              >
                Log out
              </Button>
            </Grid>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state: StoreState) => ({
  user: state.user.userDetails,
  side_nav: state.user.sideNavClose,
});

const mapDispatchToProps = {
  SidenavAutoCloseOpen,
  resetPasswordAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(SideNav);

interface SideNavProps {
  user?: userDetails;
  side_nav: boolean;
  SidenavAutoCloseOpen?: any;
  resetPasswordAction?: any;
  logoutEvent?: any;
}
